import { appFaultModel } from "@/core/modules/appFault/models/AppFaultModel";
import { batch } from "@/core/modules/batch/objects/Batch";
import { DpiHandling } from "@/features/modules/dpiHandling/objects/DpiHandling";
import { DpiHandlingHelpers } from "@/features/modules/dpiHandling/helpers/DpiHandlingHelpers";
import { EmployeeDpi } from "@/features/modules/employeeDpi/objects/EmployeeDpi";
import { employeeDpiModel } from "@/features/modules/employeeDpi/models/EmployeeDpiModel";
import { FirestoreModel } from "@/core/modules/firestore/models/FirestoreModel";
import { StoredDpi } from "@/features/modules/storedDpi/objects/StoredDpi";
import { storedDpiModel } from "@/features/modules/storedDpi/models/StoredDpiModel";

export const deliverDpisFromEmployeeToWarehouse = async (dpiHandling: DpiHandling): Promise<string> => {
  try {
    // check if dpis are available
    if (dpiHandling.sourceEmployee === undefined) throw new Error("sourceEmployee is undefined");
    const result: string = await DpiHandlingHelpers.checkDpisAvailabilityInEmployee(dpiHandling, dpiHandling.sourceEmployee.id);
    if (result !== "OK") return result;

    await FirestoreModel.runTransaction(async () => {
      if (dpiHandling.sourceEmployee === undefined) throw new Error("sourceEmployee is undefined");

      for (const handledDpi of dpiHandling.getHandledDpis()) {
        if (handledDpi.dpi === undefined) throw new Error("handledDpi dpi is undefined");
        if (handledDpi.size === undefined) throw new Error("handledDpi size is undefined");

        // check if employee dpi is available and if quantity is enough
        const employeeDpi: EmployeeDpi | undefined = await employeeDpiModel.getEmployeeDpiByDpiAndSize(
          handledDpi.dpi.id,
          handledDpi.size,
          dpiHandling.sourceEmployee.id
        );
        if (employeeDpi === undefined) throw new Error(`Dpi #${handledDpi.dpi.id} with size ${handledDpi.size} not available`);
        if (employeeDpi.quantity < handledDpi.quantity) {
          throw new Error(
            `Dpi #${handledDpi.dpi.id} with size ${handledDpi.size} not available (requested quantity: ${handledDpi.quantity}, available quantity: ${employeeDpi.quantity})`
          );
        }

        // update the employee dpi quantity or delete it if quantity is 0
        if (employeeDpi.firestoreRef === undefined) throw new Error("employeeDpi firestoreRef is undefined");
        if (employeeDpi.quantity - handledDpi.quantity > 0) {
          batch.update(employeeDpi.firestoreRef, { quantity: employeeDpi.quantity - handledDpi.quantity });
        } else {
          batch.delete(employeeDpi.firestoreRef);
        }

        // check if stored dpi already exists and update the quantity or create a new one
        const storedDpi: StoredDpi | undefined = await storedDpiModel.getStoredDpiByDpiAndSize(handledDpi.dpi.id, handledDpi.size);
        if (storedDpi === undefined) {
          const newStoredDpi: StoredDpi = new StoredDpi();
          newStoredDpi.dpi = handledDpi.dpi;
          newStoredDpi.size = handledDpi.size;
          newStoredDpi.quantity = handledDpi.quantity;
          batch.set(storedDpiModel.getDocumentReference(), newStoredDpi.toFirestore());
        } else {
          if (storedDpi.firestoreRef === undefined) throw new Error("storedDpi firestoreRef is undefined");
          batch.update(storedDpi.firestoreRef, { quantity: storedDpi.quantity + handledDpi.quantity });
        }
      }
      await batch.commit();
    });

    return "OK";
  } catch (error: unknown) {
    appFaultModel.catchAppError("DpiHandlingModel.deliverDpisFromEmployeeToWarehouse", { dpiHandling }, error);
    return "ERROR";
  }
};
